<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  type ListaItens,
  type SeletorItem,
} from '@Lar21/imaginii-web-components/vue'

import {
  encontrarOpcaoDoCatalogo,
  preservarEstadoSemSelecaoVazia,
} from '@next/compartilhados/catalogos/apresentacao/estado-seletor-catalogo'

import {
  CHAVE_I18N_CATALOGO_EMPREGADO,
  criarEstadoSeletorCatalogoEmpregado,
  criarPropriedadesPontotelSeletorEmpregado,
  extrairValorDoSeletorCatalogoEmpregado,
  filtrarOpcoesEmpregado,
  prepararEstadoSomenteLeituraEmpregado as prepararEstadoSomenteLeitura,
} from './adaptador-catalogo-empregado'
import { usarCatalogoEmpregado } from './usar-catalogo-empregado'
import type {
  FiltrosCatalogoEmpregado,
  OpcaoEmpregado,
  SelecaoCatalogoEmpregado,
} from '../catalogo-empregado.tipos'

type Props = Readonly<{
  modelValue: SelecaoCatalogoEmpregado
  selecaoMultipla?: boolean
  filtros?: FiltrosCatalogoEmpregado
  predicado?: (empregado: OpcaoEmpregado) => boolean
  desabilitado?: boolean
  somenteLeitura?: boolean
  permiteSelecionarTodos?: boolean
  permiteLimpar?: boolean
  habilitarDuploClique?: boolean
  label?: string
  placeholder?: string
  mensagemErro?: string | null
}>

const props = withDefaults(defineProps<Props>(), {
  selecaoMultipla: false,
  desabilitado: false,
  somenteLeitura: false,
  permiteSelecionarTodos: true,
  permiteLimpar: true,
  habilitarDuploClique: false,
  mensagemErro: null,
})

const emit = defineEmits<{
  'update:modelValue': [valor: SelecaoCatalogoEmpregado]
  'filtrando-itens': [filtro: string]
  'duplo-clique': [opcao: OpcaoEmpregado]
  'update:selecionouTodos': [selecionouTodos: boolean]
}>()

const { t } = useI18n()
const catalogo = usarCatalogoEmpregado(computed(() => !props.somenteLeitura))

const opcoesFiltradas = computed(() => filtrarOpcoesEmpregado(
  catalogo.itens.value,
  props.filtros,
  props.predicado,
))

const estadoSeletor = computed(() => prepararEstadoSomenteLeitura(
  criarEstadoSeletorCatalogoEmpregado(
    opcoesFiltradas.value,
    props.modelValue,
    props.selecaoMultipla,
  ),
  props.somenteLeitura,
  props.modelValue,
))

const erroExibido = computed(() => catalogo.erro.value
  ? t(`${CHAVE_I18N_CATALOGO_EMPREGADO}.erroBusca`)
  : props.mensagemErro)

const propriedadesSeletor = computed(() => (
  criarPropriedadesPontotelSeletorEmpregado({
    traduzir: t,
    ...(props.label !== undefined ? { label: props.label } : {}),
    ...(props.placeholder !== undefined
      ? { placeholder: props.placeholder }
      : {}),
    erro: erroExibido.value,
  })
))

const alterarSelecao = (estado: ListaItens): void => {
  emit(
    'update:modelValue',
    extrairValorDoSeletorCatalogoEmpregado(
      preservarEstadoSemSelecaoVazia(
        estado,
        estadoSeletor.value,
        props.permiteLimpar,
      ),
      opcoesFiltradas.value,
      props.selecaoMultipla,
    ),
  )
}

const tratarDuploClique = (item: SeletorItem): void => {
  const opcao = encontrarOpcaoDoCatalogo(opcoesFiltradas.value, item)
  if (opcao) emit('duplo-clique', opcao)
}

</script>

<template>
  <PontotelSeletor
    v-bind="propriedadesSeletor"
    :model-value="estadoSeletor"
    :carregar-itens="null"
    :selecao-multipla="selecaoMultipla"
    :desabilitado="desabilitado"
    :modo-exibicao="somenteLeitura"
    :permite-selecionar-todos="permiteSelecionarTodos"
    :habilitar-duplo-clique="habilitarDuploClique"
    :recarregar-todos="!somenteLeitura"
    :recarregando-todos="catalogo.atualizando.value"
    @update:model-value="alterarSelecao"
    @filtrando-itens="emit('filtrando-itens', $event)"
    @duplo-clique="tratarDuploClique"
    @update:selecionou-todos="emit('update:selecionouTodos', $event)"
    @recarregar-todos="catalogo.atualizar"
  />
</template>
